/**
 * In-memory store of one sheet's layers and annotations for the signed-in user, read by React
 * through useSyncExternalStore. Opening it loads Dexie first (works offline), then the engine
 * pulls from the server. Every edit updates memory, Dexie and the outbox (one Dexie transaction,
 * queued in the generation's write chain) and records an undo step. Only live objects are kept
 * (memory and Dexie): a delete removes the object, and its tombstone travels in the outbox.
 *
 * Snapshots are immutable and rebuilt lazily; per-page object arrays, the connector list and
 * unchanged LayerViews keep their identity between snapshots (see derive.ts).
 */
import {
  MAX_LIVE_OBJECTS_PER_LAYER,
  objectToRow,
  type AnnotationKind,
  type AnnotationObject,
  type AnnotationOf,
  type DataByKind,
} from '../../../shared/annotations';
import { roleAtLeast } from '../../../shared/audience';
import type { LayerDTO, LayerShareTarget, PullResume } from '../../../shared/dto/annotations';
import type { MeDTO } from '../../../shared/dto/auth';
import type { SongFileDTO } from '../../../shared/dto/library';
import { isUuid, uuid } from '../../../shared/ids';
import { ApiFailure } from '../../api/client';
import type { Change, LayerView, SheetAnnotations, SyncStatus } from '../contract';
import { cursorRow, META, restoreCursors, type AnnotationDb, type CursorRow } from './db';
import { ObjectIndex } from './derive';
import type { AnnotationEngine, Generation } from './engine';
import { knownLayers, mergePull, resumeToSend, type PullMergeResult } from './merge';
import {
  cleanLayerName,
  defaultVisible,
  isKind,
  isStale,
  isValidPage,
  keepLocal,
  layerFromDTO,
  sameLayer,
  sortLayers,
  tombstone,
  validData,
  zOrder,
  type LayerRecord,
} from './model';
import { annKey, layerKey, type OpRecord } from './queue';
import { classifyError, transport, type NetOutcome } from './transport';
import { UndoHistory, invertStep, stepFromTouched, type Touched } from './undo';

/** Upper bound of pull pages per sync (each page ≤ 5000 rows). */
const MAX_PULL_ROUNDS = 25;
/** How long a released store stays in memory (re-opening the song within this time is instant). */
const DISPOSE_AFTER_MS = 10_000;
const DEFAULT_LAYER_NAME = 'Mitt lag';

type Methods = Omit<
  SheetAnnotations,
  'status' | 'layers' | 'activeLayerId' | 'objectsByPage' | 'connectors' | 'canUndo' | 'canRedo' | 'sync' | 'hasStaleLayers' | 'hideStaleLayers'
>;

interface Owner {
  id: string;
  name: string;
  isEditor: boolean;
}

const ownerOf = (me: MeDTO): Owner => ({ id: me.id, name: me.fullName, isEditor: roleAtLeast(me.role, 'editor') });

const sameArray = <T>(a: readonly T[], b: readonly T[]) => a.length === b.length && a.every((x, i) => x === b[i]);

export class SheetStore {
  readonly fileId: string;
  dead = false;
  private killed = false;

  private refs = 0;
  private disposeTimer: ReturnType<typeof setTimeout> | null = null;
  private fileRev: number;
  private pageCount: number | null;
  private me: Owner;
  private defaultLayerName = DEFAULT_LAYER_NAME;

  private status: SheetAnnotations['status'] = 'loading';
  /** Local load finished (successfully or not): edits are accepted from now on. */
  private loaded = false;
  private readonly whenLoaded: Promise<void>;
  private localFailed = false;
  /** The server refused to pull this sheet (403/404): shown as sync error. */
  private pullRejected = false;
  private lastSyncAt: number | null = null;

  private layers = new Map<string, LayerRecord>();
  private prefs = new Map<string, boolean>();
  /**
   * "Fela lög af eldri útgáfu" (per sheet, per device): hides every stale layer (isStaleLayer)
   * without touching the per-layer prefs, so it stops applying by itself once a layer is confirmed
   * and turning it off restores exactly the earlier state. `staleShown` = stale layers the user
   * showed anyway while it is on (eye, drawing on the layer, "Skoða fyrst").
   */
  private hideStale = false;
  private staleShown = new Set<string>();
  /** The filter was changed here before the local load finished (the stored value is older). */
  private hideStaleChanged = false;
  private cursors = new Map<string, number>();
  /**
   * Continuations of paged full fetches (PullResponse.resume), sent back with the next pull. Kept
   * in memory only: when the store is dropped the layer's partial content stays without a cursor,
   * so the next pull fetches it in full again (and clears what it doesn't deliver).
   */
  private resume = new Map<string, PullResume>();
  private index = new ObjectIndex();
  private history = new UndoHistory();
  private activeLayerId: string | null = null;

  private listeners = new Set<() => void>();
  private emitQueued = false;
  private version = 0;
  private snapVersion = -1;
  private snapshot: SheetAnnotations | null = null;
  private layersDirty = true;
  private sorted: LayerRecord[] = [];
  private views: LayerView[] = [];
  private viewCache = new Map<string, { rec: LayerRecord; visible: boolean; pending: number; stale: boolean; view: LayerView }>();
  private syncCache: SheetAnnotations['sync'] | null = null;
  private readonly methods: Methods;

  constructor(
    private readonly engine: AnnotationEngine,
    readonly gen: Generation,
    me: MeDTO,
    file: SongFileDTO,
  ) {
    this.fileId = file.id;
    this.fileRev = file.rev;
    this.pageCount = file.pageCount ?? file.pages?.length ?? null;
    this.me = ownerOf(me);
    this.methods = {
      setActiveLayer: this.setActiveLayer,
      get: (id) => this.index.get(id),
      isMine: (id) => {
        const o = this.index.get(id);
        return !!o && this.isOwnLayer(o.layerId);
      },
      newObject: this.newObject,
      apply: (changes) => {
        this.applyChanges(changes, true);
      },
      undo: this.undo,
      redo: this.redo,
      createLayer: (name) => this.createLayerInternal(cleanLayerName(name, this.defaultLayerName), this.fileRev, true),
      renameLayer: this.renameLayer,
      deleteLayer: this.deleteLayer,
      setLayerVisible: this.setLayerVisible,
      shareLayer: this.shareLayer,
      copyToMine: this.copyToMine,
      confirmLayerRevision: this.confirmLayerRevision,
      setHideStaleLayers: this.setHideStaleLayers,
    };
    this.whenLoaded = this.load();
    // A store created during a render that never commits must not leak.
    this.disposeTimer = setTimeout(this.dispose, DISPOSE_AFTER_MS);
  }

  // ---- lifecycle ------------------------------------------------------------------------

  get isOpen(): boolean {
    return this.refs > 0 && !this.dead && this.loaded;
  }

  retain(): void {
    // Disposed before the mounting component committed (very slow commit): come back if possible.
    if (this.dead && (this.killed || !this.engine.adopt(this))) return;
    this.dead = false;
    this.refs++;
    if (this.disposeTimer) clearTimeout(this.disposeTimer);
    this.disposeTimer = null;
    if (this.refs === 1) {
      void this.whenLoaded.then(() => {
        if (this.refs > 0 && !this.dead) void this.engine.schedule({ sheets: [this.fileId] });
      });
    }
  }

  release(): void {
    if (this.dead || this.refs === 0) return;
    this.refs--;
    if (this.refs > 0) return;
    this.engine.flushSoon();
    if (this.disposeTimer) clearTimeout(this.disposeTimer);
    this.disposeTimer = setTimeout(this.dispose, DISPOSE_AFTER_MS);
  }

  private dispose = (): void => {
    this.disposeTimer = null;
    if (this.refs > 0 || this.dead) return;
    this.dead = true;
    this.engine.dropStore(this);
  };

  /** The generation is gone (logout / user switch): stop touching anything. */
  kill(): void {
    this.killed = true;
    this.dead = true;
    if (this.disposeTimer) clearTimeout(this.disposeTimer);
    this.disposeTimer = null;
    this.touch();
  }

  setFile(file: SongFileDTO): void {
    const pc = file.pageCount ?? file.pages?.length ?? null;
    if (file.rev === this.fileRev && pc === this.pageCount) return;
    this.fileRev = file.rev;
    this.pageCount = pc;
    this.touch();
  }

  setMe(me: MeDTO): void {
    if (me.id !== this.me.id) return;
    const next = ownerOf(me);
    if (next.name === this.me.name && next.isEditor === this.me.isEditor) return;
    this.me = next;
  }

  setDefaultLayerName(name: string): void {
    if (name) this.defaultLayerName = name;
  }

  private async load(): Promise<void> {
    const gen = this.gen;
    await gen.ready;
    if (this.dead || gen.dead) return;
    if (!gen.db) {
      this.localFailed = true;
    } else {
      const fileId = this.fileId;
      const data = await gen.run(async (db) => {
        const layers = await db.layers.where('sheetFileId').equals(fileId).toArray();
        const ids = layers.map((l) => l.id);
        const [objects, cursors, prefs, active, hideStale] = await Promise.all([
          ids.length ? db.objects.where('layerId').anyOf(ids).toArray() : Promise.resolve([] as AnnotationObject[]),
          db.cursors.bulkGet(ids),
          db.prefs.bulkGet(ids),
          db.meta.get(META.active(fileId)),
          db.meta.get(META.hideStale(fileId)),
        ]);
        return { layers, objects, cursors, prefs, active, hideStale };
      });
      if (this.dead || gen.dead) return;
      if (!data) {
        this.localFailed = true;
      } else {
        for (const l of data.layers) if (!this.layers.has(l.id)) this.layers.set(l.id, l);
        restoreCursors(this.cursors, data.cursors);
        for (const p of data.prefs) if (p && !this.prefs.has(p.layerId)) this.prefs.set(p.layerId, p.visible);
        const tombstones: string[] = [];
        for (const o of data.objects) {
          if (o.deletedAt !== null) tombstones.push(o.id); // never kept locally (pending deletes live in the outbox)
          else if (!this.index.getAny(o.id)) this.index.put(o);
        }
        if (tombstones.length) void gen.run((db) => db.objects.bulkDelete(tombstones));
        const act = data.active?.v;
        if (typeof act === 'string' && this.isOwnLayer(act)) this.activeLayerId = act;
        const hs = data.hideStale?.v as { shown?: unknown } | undefined;
        if (!this.hideStaleChanged && hs && typeof hs === 'object') {
          this.hideStale = true;
          this.staleShown = new Set(Array.isArray(hs.shown) ? hs.shown.filter((x): x is string => typeof x === 'string') : []);
          this.index.invalidateAll();
        }
      }
    }
    this.loaded = true;
    if (!this.localFailed) this.status = 'ready';
    this.ensureActive();
    this.touch(true);
  }

  // ---- React binding ----------------------------------------------------------------------

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): SheetAnnotations => {
    if (this.snapshot && this.snapVersion === this.version) return this.snapshot;
    this.snapVersion = this.version;
    const userId = this.me.id;
    if (this.layersDirty) {
      this.sorted = sortLayers(this.layers.values(), userId);
      this.layersDirty = false;
    }
    const stats = this.gen.outbox.stats(userId);
    const views = this.sorted.map((l) => this.viewOf(l, stats.byLayer.get(l.id) ?? 0));
    if (!sameArray(views, this.views)) this.views = views;

    const visible = this.sorted.filter((l) => this.isVisible(l));
    const { objectsByPage, connectors } = this.index.views(zOrder(visible, userId));
    const hasStaleLayers = visible.some((l) => this.isStaleLayer(l));

    const sheet = stats.bySheet.get(this.fileId);
    // A local write failed and edits are still unsent: they live in memory only (closing the app
    // offline would lose them) — never claim "saved on this device" then.
    const unsafe = this.gen.writeFailed && this.gen.outbox.countFor(userId) > 0;
    const status: SyncStatus = unsafe
      ? 'error'
      : this.engine.running
        ? 'syncing'
        : this.engine.phase === 'offline'
          ? 'offline'
          : this.engine.phase !== 'idle' || this.pullRejected || (sheet?.failed ?? 0) > 0
            ? 'error'
            : 'idle';
    const pending = sheet?.pending ?? 0;
    const sc = this.syncCache;
    if (!sc || sc.status !== status || sc.pending !== pending || sc.lastSyncAt !== this.lastSyncAt || !!sc.unsavedLocally !== unsafe) {
      this.syncCache = { status, pending, lastSyncAt: this.lastSyncAt, unsavedLocally: unsafe, flush: this.flush };
    }

    const prev = this.snapshot;
    if (
      prev &&
      prev.status === this.status &&
      prev.layers === this.views &&
      prev.activeLayerId === this.activeLayerId &&
      prev.objectsByPage === objectsByPage &&
      prev.connectors === connectors &&
      prev.canUndo === this.history.canUndo &&
      prev.canRedo === this.history.canRedo &&
      prev.sync === this.syncCache &&
      prev.hasStaleLayers === hasStaleLayers &&
      prev.hideStaleLayers === this.hideStale
    ) {
      return prev; // nothing the UI can see changed: no re-render
    }
    this.snapshot = {
      status: this.status,
      layers: this.views,
      activeLayerId: this.activeLayerId,
      objectsByPage,
      connectors,
      canUndo: this.history.canUndo,
      canRedo: this.history.canRedo,
      sync: this.syncCache!,
      hasStaleLayers,
      hideStaleLayers: this.hideStale,
      ...this.methods,
    };
    return this.snapshot;
  };

  /** Engine state changed (sync status, outbox counts). */
  onEngineState(): void {
    this.touch();
  }

  private touch(layers = false): void {
    this.version++;
    if (layers) this.layersDirty = true;
    if (this.emitQueued) return;
    this.emitQueued = true;
    queueMicrotask(() => {
      this.emitQueued = false;
      for (const fn of this.listeners) fn();
    });
  }

  private viewOf(rec: LayerRecord, pending: number): LayerView {
    const visible = this.isVisible(rec);
    const stale = this.isStaleLayer(rec);
    const c = this.viewCache.get(rec.id);
    if (c && c.rec === rec && c.visible === visible && c.pending === pending && c.stale === stale) return c.view;
    const mine = rec.ownerId === this.me.id;
    const view: LayerView = {
      id: rec.id,
      sheetFileId: rec.sheetFileId,
      ownerId: rec.ownerId,
      ownerName: rec.ownerName,
      ownerIsEditor: rec.ownerIsEditor,
      ownerInactive: rec.ownerInactive,
      mine,
      name: rec.name,
      sheetRev: rec.sheetRev,
      stale,
      visible,
      shares: mine ? rec.shares : [],
      pending,
    };
    this.viewCache.set(rec.id, { rec, visible, pending, stale, view });
    return view;
  }

  // ---- helpers --------------------------------------------------------------------------

  private get editable(): boolean {
    return this.loaded && !this.dead && !this.gen.dead;
  }

  private isOwnLayer(id: string): boolean {
    return this.layers.get(id)?.ownerId === this.me.id;
  }

  /**
   * The one "gert á eldri útgáfu" rule (badge, banner, review, hide filter): drawn on an older
   * revision of the sheet AND has marks — an empty layer has nothing that could be out of place.
   */
  private isStaleLayer(l: LayerRecord): boolean {
    return isStale(l, this.fileRev) && this.index.liveCount(l.id) > 0;
  }

  private isVisible(l: LayerRecord): boolean {
    if (this.hideStale && !this.staleShown.has(l.id) && this.isStaleLayer(l)) return false;
    return this.prefs.get(l.id) ?? defaultVisible(l, this.me.id);
  }

  /**
   * While "Fela lög af eldri útgáfu" is on, a layer on an older revision the user wants to see (or
   * draws on) is let through the filter. Any older-revision layer counts — an empty one would
   * otherwise vanish with its first mark. Returns true when the set changed (persist it).
   */
  private letThroughStaleFilter(id: string): boolean {
    const rec = this.layers.get(id);
    if (!this.hideStale || !rec || !isStale(rec, this.fileRev) || this.staleShown.has(id)) return false;
    this.staleShown.add(id);
    return true;
  }

  /** Stores the hide-stale filter of this sheet (no row = off); forgets layers that are gone. */
  private saveHideStale(): void {
    for (const id of this.staleShown) if (!this.layers.has(id)) this.staleShown.delete(id);
    const k = META.hideStale(this.fileId);
    const v = this.hideStale ? { shown: [...this.staleShown] } : null;
    void this.gen.run(async (db) => {
      if (v) await db.meta.put({ k, v });
      else await db.meta.delete(k);
    });
  }

  private ownLayersSorted(): LayerRecord[] {
    if (this.layersDirty) {
      this.sorted = sortLayers(this.layers.values(), this.me.id);
      this.layersDirty = false;
    }
    return this.sorted.filter((l) => l.ownerId === this.me.id);
  }

  /** Keeps activeLayerId pointing at an existing own layer (or null). */
  private ensureActive(): void {
    if (this.activeLayerId && this.isOwnLayer(this.activeLayerId)) return;
    this.activeLayerId = this.ownLayersSorted()[0]?.id ?? null;
  }

  private pendingLayerState(): { localOnly: Set<string>; locallyDeleted: Set<string> } {
    const localOnly = new Set<string>();
    const locallyDeleted = new Set<string>();
    for (const op of this.gen.outbox.ordered()) {
      if (op.sheetFileId !== this.fileId) continue;
      // A queued upsert of a layer a pull already delivered (it has a cursor) only confirms a sheet
      // revision: the layer exists on the server and keeps syncing as a known layer.
      if (op.type === 'layer.upsert') {
        if (!this.cursors.has(op.layerId)) localOnly.add(op.layerId);
      } else if (op.type === 'layer.delete') locallyDeleted.add(op.layerId);
    }
    return { localOnly, locallyDeleted };
  }

  // ---- objects --------------------------------------------------------------------------

  newObject = <K extends AnnotationKind>(page: number, kind: K, data: DataByKind[K]): AnnotationOf<K> => {
    const layerId = this.editable ? this.targetLayer() : (this.activeLayerId ?? '');
    return { id: uuid(), layerId, page, kind, data, hlcMs: 0, deviceId: this.gen.deviceId, deletedAt: null } as unknown as AnnotationOf<K>;
  };

  /** The own layer that receives new objects: the active one, the first own one, or a new "Mitt lag". */
  private targetLayer(): string {
    this.ensureActive();
    let id = this.activeLayerId;
    if (!id) id = this.createLayerInternal(cleanLayerName(this.defaultLayerName, DEFAULT_LAYER_NAME), this.fileRev, true);
    const rec = this.layers.get(id);
    if (rec && this.letThroughStaleFilter(id)) {
      this.saveHideStale();
      this.index.invalidateAll();
    }
    if (rec && !this.isVisible(rec)) this.setLayerVisible(id, true);
    return id;
  }

  /** Validates one change against the current state; returns the new object state or null (dropped). */
  private resolve(ch: Change, now: number): AnnotationObject | null {
    const gen = this.gen;
    if (!ch || typeof ch !== 'object') return null;
    if (ch.type === 'add') {
      const o = ch.obj;
      if (!o || !isUuid(o.id) || !isKind(o.kind) || !this.isOwnLayer(o.layerId) || !isValidPage(o.page, this.pageCount)) return null;
      const prev = this.index.getAny(o.id);
      if (prev && prev.layerId !== o.layerId) return null;
      const wasLive = !!prev && prev.deletedAt === null;
      if (!wasLive && this.index.liveCount(o.layerId) >= MAX_LIVE_OBJECTS_PER_LAYER) return null;
      const data = validData(o.kind, o.data);
      if (!data) return null;
      return { id: o.id, layerId: o.layerId, page: o.page, kind: o.kind, data, hlcMs: gen.clock.tick(), deviceId: gen.deviceId, deletedAt: null } as AnnotationObject;
    }
    if (ch.type === 'update') {
      const prev = this.index.get(ch.id);
      if (!prev || !this.isOwnLayer(prev.layerId)) return null;
      const page = ch.page ?? prev.page;
      if (!isValidPage(page, this.pageCount)) return null;
      const data = validData(prev.kind, ch.data);
      if (!data) return null;
      return { ...prev, page, data, hlcMs: gen.clock.tick(), deviceId: gen.deviceId } as AnnotationObject;
    }
    if (ch.type === 'delete') {
      const prev = this.index.get(ch.id);
      if (!prev || !this.isOwnLayer(prev.layerId)) return null;
      return tombstone(prev, gen.clock.tick(), gen.deviceId, now);
    }
    return null;
  }

  /** Applies changes to own layers: memory + Dexie objects + outbox in one transaction. */
  private applyChanges(changes: readonly Change[], record: boolean): Touched[] {
    if (!this.editable || !Array.isArray(changes) || !changes.length) return [];
    const gen = this.gen;
    const now = Date.now();
    const touched = new Map<string, Touched>();
    for (const ch of changes) {
      const next = this.resolve(ch, now);
      if (!next) continue;
      const t = touched.get(next.id);
      if (t) t.after = next;
      else touched.set(next.id, { id: next.id, before: this.index.getAny(next.id), after: next });
      // A deleted object leaves memory and disk; its tombstone travels in the outbox only.
      if (next.deletedAt === null) this.index.put(next);
      else this.index.remove(next.id);
    }
    if (!touched.size) return [];
    const list = [...touched.values()];
    const objs = list.map((t) => t.after!);
    const ops = objs.map((o) =>
      gen.outbox.enqueue({ type: 'ann', payload: objectToRow(o), userId: this.me.id, sheetFileId: this.fileId, layerId: o.layerId }, now),
    );
    const live = objs.filter((o) => o.deletedAt === null);
    const gone = objs.filter((o) => o.deletedAt !== null).map((o) => o.id);
    const lastHlc = gen.clock.value;
    void gen.run(async (db) => {
      if (live.length) await db.objects.bulkPut(live);
      if (gone.length) await db.objects.bulkDelete(gone);
      await db.outbox.bulkPut(ops);
      await db.meta.put({ k: META.lastHlc, v: lastHlc });
    });
    if (record) {
      const step = stepFromTouched(list);
      if (step) this.history.record(step);
    }
    this.engine.noteEdit();
    this.touch();
    return list;
  }

  undo = (): void => {
    if (!this.editable) return;
    for (;;) {
      const step = this.history.popUndo();
      if (!step) break;
      const done = stepFromTouched(this.applyChanges(step.undo, false));
      if (done) {
        this.history.pushRedo(invertStep(done));
        break;
      }
    }
    this.touch();
  };

  redo = (): void => {
    if (!this.editable) return;
    for (;;) {
      const step = this.history.popRedo();
      if (!step) break;
      const done = stepFromTouched(this.applyChanges(step.redo, false));
      if (done) {
        this.history.pushUndo(done);
        break;
      }
    }
    this.touch();
  };

  /** The server refused our version of an object: drop it and refetch the layer in full. */
  revertObject(objectId: string, layerId: string): void {
    if (this.dead) return;
    if (this.gen.outbox.has(annKey(objectId))) return; // edited again since: that op decides
    this.index.remove(objectId);
    this.resume.delete(layerId);
    const hadCursor = this.cursors.delete(layerId);
    void this.gen.run(async (db) => {
      await db.objects.delete(objectId);
      if (hadCursor) await db.cursors.delete(layerId);
    });
    this.touch();
  }

  // ---- layers ---------------------------------------------------------------------------

  private createLayerInternal(name: string, sheetRev: number, activate: boolean): string {
    if (!this.editable) return '';
    const gen = this.gen;
    const now = Date.now();
    const id = uuid();
    const rec: LayerRecord = {
      id,
      sheetFileId: this.fileId,
      ownerId: this.me.id,
      ownerName: this.me.name,
      ownerIsEditor: this.me.isEditor,
      ownerInactive: false,
      name,
      sheetRev,
      updatedAt: now,
      maxSeq: 0,
      shares: [],
      firstSeen: now,
    };
    this.layers.set(id, rec);
    this.prefs.set(id, true);
    const op = gen.outbox.enqueue(
      { type: 'layer.upsert', payload: { id, sheetFileId: this.fileId, name, sheetRev }, userId: this.me.id, sheetFileId: this.fileId, layerId: id },
      now,
    );
    if (activate) this.activeLayerId = id;
    const fileId = this.fileId;
    void gen.run(async (db) => {
      await db.layers.put(rec);
      await db.prefs.put({ layerId: id, visible: true });
      await db.outbox.put(op);
      if (activate) await db.meta.put({ k: META.active(fileId), v: id });
    });
    this.engine.noteEdit();
    this.touch(true);
    return id;
  }

  setActiveLayer = (id: string): void => {
    if (!this.editable || !this.isOwnLayer(id)) return;
    const rec = this.layers.get(id)!;
    if (!this.isVisible(rec)) this.setLayerVisible(id, true);
    if (this.activeLayerId === id) return;
    this.activeLayerId = id;
    const fileId = this.fileId;
    void this.gen.run((db) => db.meta.put({ k: META.active(fileId), v: id }));
    this.touch();
  };

  renameLayer = (id: string, name: string): void => {
    if (!this.editable || !this.isOwnLayer(id)) return;
    const cur = this.layers.get(id)!;
    const n = cleanLayerName(name, cur.name);
    if (n === cur.name) return;
    const rec: LayerRecord = { ...cur, name: n, updatedAt: Date.now() };
    this.layers.set(id, rec);
    const op = this.gen.outbox.enqueue(
      { type: 'layer.rename', payload: { name: n }, userId: this.me.id, sheetFileId: this.fileId, layerId: id },
      Date.now(),
    );
    void this.gen.run(async (db) => {
      await db.layers.put(rec);
      await db.outbox.put(op);
    });
    this.engine.noteEdit();
    this.touch(true);
  };

  deleteLayer = (id: string): void => {
    if (!this.editable || !this.isOwnLayer(id)) return;
    const gen = this.gen;
    const removedIds = this.index.removeLayer(id);
    this.layers.delete(id);
    this.prefs.delete(id);
    this.cursors.delete(id);
    this.resume.delete(id);
    this.viewCache.delete(id);
    const dropped = gen.outbox.removeWhere((op) => op.type === 'ann' && op.layerId === id);
    // Collapses into a queued create (never sent → the server answers 404, which counts as done).
    const op = gen.outbox.enqueue({ type: 'layer.delete', payload: null, userId: this.me.id, sheetFileId: this.fileId, layerId: id }, Date.now());
    this.history.prune(new Set(removedIds));
    const wasActive = this.activeLayerId === id;
    if (wasActive) {
      this.activeLayerId = null;
      this.layersDirty = true;
      this.ensureActive();
    }
    const fileId = this.fileId;
    const nextActive = this.activeLayerId;
    void gen.run(async (db) => {
      await db.layers.delete(id);
      await db.objects.where('layerId').equals(id).delete();
      await db.cursors.delete(id);
      await db.prefs.delete(id);
      await db.deleteOps(dropped);
      await db.outbox.put(op);
      if (wasActive) {
        if (nextActive) await db.meta.put({ k: META.active(fileId), v: nextActive });
        else await db.meta.delete(META.active(fileId));
      }
    });
    this.engine.noteEdit();
    this.touch(true);
  };

  setLayerVisible = (id: string, visible: boolean): void => {
    if (this.dead || !this.layers.has(id)) return;
    // Showing a layer the hide-stale filter holds back lets it through; hiding it puts it back.
    const filterChanged = visible ? this.letThroughStaleFilter(id) : this.hideStale && this.staleShown.delete(id);
    const prefChanged = this.prefs.get(id) !== visible;
    if (!prefChanged && !filterChanged) return;
    if (prefChanged) {
      this.prefs.set(id, visible);
      void this.gen.run((db) => db.prefs.put({ layerId: id, visible }));
    }
    if (filterChanged) this.saveHideStale();
    this.index.invalidateAll();
    this.touch();
  };

  /**
   * "Fela lög af eldri útgáfu" for this sheet (on this device). On: every layer drawn on an older
   * revision is hidden (also ones shown anyway before — a second "hide" hides them again); layers
   * stop being hidden by it as soon as they are confirmed. Off: back to each layer's own setting.
   */
  setHideStaleLayers = (hide: boolean): void => {
    if (this.dead) return;
    if (this.hideStale === hide && this.staleShown.size === 0) return;
    this.hideStale = hide;
    this.staleShown.clear();
    this.hideStaleChanged = true;
    this.saveHideStale();
    this.index.invalidateAll();
    this.touch();
  };

  shareLayer = async (id: string, shares: LayerShareTarget[]): Promise<void> => {
    if (this.dead || !this.isOwnLayer(id)) throw new ApiFailure(404, 'not_found');
    // A layer created on this device must exist on the server before it can be shared.
    if (this.gen.outbox.has(layerKey(id))) await this.engine.schedule({});
    const dto = await transport.shareLayer(id, { shares });
    const cur = this.layers.get(id);
    if (!cur || this.dead) return;
    const rec: LayerRecord = { ...cur, shares: Array.isArray(dto?.shares) ? dto.shares : shares, updatedAt: dto?.updatedAt ?? cur.updatedAt };
    this.layers.set(id, rec);
    void this.gen.run((db) => db.layers.put(rec));
    this.touch(true);
  };

  copyToMine = (id: string, name: string): string => {
    const src = this.layers.get(id);
    // Like createLayer: '' when it can't act (still loading, or the layer is gone / unshared).
    if (!src || !this.editable) return '';
    const gen = this.gen;
    const newId = this.createLayerInternal(cleanLayerName(name, src.name), src.sheetRev, true);
    if (!newId) return '';
    // A copy of a layer on an older revision is stale too: the user just asked for it, show it.
    if (this.letThroughStaleFilter(newId)) this.saveHideStale();
    const now = Date.now();
    // Oldest first, so the copies keep the original stacking order (z-order follows hlcMs).
    const originals = this.index
      .layerObjects(id)
      .sort((a, b) => a.hlcMs - b.hlcMs || (a.id < b.id ? -1 : 1))
      .slice(0, MAX_LIVE_OBJECTS_PER_LAYER);
    if (!originals.length) return newId;
    const copies = originals.map(
      (o) => ({ ...o, id: uuid(), layerId: newId, hlcMs: gen.clock.tick(), deviceId: gen.deviceId, deletedAt: null }) as AnnotationObject,
    );
    for (const c of copies) this.index.put(c);
    const ops = copies.map((c) =>
      gen.outbox.enqueue({ type: 'ann', payload: objectToRow(c), userId: this.me.id, sheetFileId: this.fileId, layerId: newId }, now),
    );
    const lastHlc = gen.clock.value;
    void gen.run(async (db) => {
      await db.objects.bulkPut(copies);
      await db.outbox.bulkPut(ops);
      await db.meta.put({ k: META.lastHlc, v: lastHlc });
    });
    this.engine.noteEdit();
    this.touch(true);
    return newId;
  };

  /**
   * "Staðfesta": the owner checked that a layer drawn on an older sheet revision still fits.
   * Queues the layer upsert again with the new sheetRev (same FIFO outbox as the other layer ops,
   * so it is sent offline-safe and ahead of later edits); collapses into a queued create/rename.
   */
  confirmLayerRevision = (id: string, rev?: number): void => {
    if (!this.editable || !this.isOwnLayer(id)) return;
    const cur = this.layers.get(id)!;
    const target = Math.min(rev ?? this.fileRev, this.fileRev);
    if (!Number.isInteger(target) || target <= cur.sheetRev) return;
    const now = Date.now();
    const rec: LayerRecord = { ...cur, sheetRev: target, updatedAt: now };
    this.layers.set(id, rec);
    const op = this.gen.outbox.enqueue(
      {
        type: 'layer.upsert',
        // keepName: the server keeps the current name (a rename made on another device stays);
        // a queued create/rename this collapses into still sends the name (see collapseBody).
        payload: { id, sheetFileId: this.fileId, name: rec.name, sheetRev: target, keepName: true },
        userId: this.me.id,
        sheetFileId: this.fileId,
        layerId: id,
      },
      now,
    );
    void this.gen.run(async (db) => {
      await db.layers.put(rec);
      await db.outbox.put(op);
    });
    this.engine.noteEdit();
    this.touch(true);
  };

  /** Layer metadata confirmed by POST /layers. */
  onLayerConfirmed(dto: LayerDTO): void {
    const cur = this.layers.get(dto.id);
    if (!cur || this.dead) return;
    const rec = layerFromDTO(dto, cur, Date.now(), keepLocal(this.gen.outbox.get(layerKey(dto.id))));
    if (sameLayer(cur, rec)) return;
    this.layers.set(dto.id, rec);
    void this.gen.run((db) => db.layers.put(rec));
    this.touch(true);
  }

  // ---- sync -----------------------------------------------------------------------------

  private flush = async (): Promise<void> => {
    if (this.dead) return;
    await this.engine.schedule({ sheets: [this.fileId], retryFailed: true });
  };

  /** Pulls this sheet until the server has nothing more (called by the engine's sync loop). */
  async pull(): Promise<NetOutcome> {
    const gen = this.gen;
    for (let round = 0; round < MAX_PULL_ROUNDS; round++) {
      if (this.dead || gen.dead) return 'ok';
      const before = this.pendingLayerState();
      const known = knownLayers(this.cursors, before.localOnly);
      const resume = resumeToSend(this.resume, known, before.localOnly);
      const resumed = new Set(Object.keys(resume));
      let res;
      try {
        res = await transport.pull({ sheetFileId: this.fileId, knownLayers: known, ...(resumed.size ? { resume } : {}) });
      } catch (e) {
        const c = classifyError(e);
        if (c === 'fail') this.onPullRejected(e);
        else if (this.localFailed && this.status === 'loading') {
          this.status = 'error';
          this.touch();
        }
        return c;
      }
      if (this.dead || gen.dead) return 'ok';
      const after = this.pendingLayerState(); // edits may have happened while the request was out
      const m = mergePull({
        response: res,
        sent: new Map(Object.entries(known)),
        resumed,
        localLayers: [...this.layers.keys()],
        pending: (id) => {
          const op = gen.outbox.get(annKey(id));
          return op && op.type === 'ann' ? op.payload : undefined;
        },
        local: (id) => this.index.get(id),
        locallyDeleted: after.locallyDeleted,
        localOnly: after.localOnly,
      });
      this.applyPull(m);
      if (!m.again) break;
    }
    this.pullRejected = false;
    this.lastSyncAt = Date.now();
    if (this.status !== 'ready') this.status = 'ready';
    this.touch();
    return 'ok';
  }

  private applyPull(m: PullMergeResult): void {
    const gen = this.gen;
    const now = Date.now();
    const delLayers: string[] = [];
    const delObjects: string[] = [];
    const putLayers: LayerRecord[] = [];
    const putCursors: CursorRow[] = [];
    const delCursors: string[] = [];
    const droppedOps: OpRecord[] = [];
    let layersChanged = false;

    for (const id of m.removedLayers) {
      const ids = this.index.removeLayer(id);
      if (this.layers.delete(id)) layersChanged = true;
      this.cursors.delete(id);
      this.resume.delete(id);
      this.prefs.delete(id);
      this.viewCache.delete(id);
      delLayers.push(id);
      if (ids.length) this.history.prune(new Set(ids));
      droppedOps.push(...gen.outbox.removeWhere((op) => op.layerId === id && op.sheetFileId === this.fileId));
    }
    for (const id of m.fullLayers) {
      delObjects.push(...this.index.clearLayer(id, (oid) => m.keep.has(oid) || gen.outbox.has(annKey(oid))));
    }
    for (const dto of m.layers) {
      const cur = this.layers.get(dto.id);
      const rec = layerFromDTO(dto, cur, now, keepLocal(gen.outbox.get(layerKey(dto.id))));
      if (!cur || !sameLayer(cur, rec)) {
        this.layers.set(rec.id, rec);
        putLayers.push(rec);
        layersChanged = true;
      }
    }
    const putObjects = m.put;
    for (const o of putObjects) this.index.put(o);
    for (const id of m.drop) {
      this.index.remove(id);
      delObjects.push(id);
    }
    for (const id of m.dropPending) {
      const r = gen.outbox.remove(annKey(id));
      if (r) droppedOps.push(r);
    }
    for (const [id, c] of m.cursors) {
      if (c === null) {
        if (this.cursors.delete(id)) delCursors.push(id);
      } else if (this.cursors.get(id) !== c) {
        this.cursors.set(id, c);
        putCursors.push(cursorRow(id, c));
      }
    }
    // The server's continuation set replaces ours (it covers every visible layer without a cursor).
    this.resume = m.resume;
    if (m.maxHlc) gen.clock.observe(m.maxHlc);
    const lastHlc = gen.clock.value;

    const activeGone = !!this.activeLayerId && !this.isOwnLayer(this.activeLayerId);
    if (activeGone || (!this.activeLayerId && layersChanged)) {
      // e.g. own layers arriving on a new device: the first one receives new objects.
      this.activeLayerId = null;
      this.layersDirty = true;
      this.ensureActive();
    }

    const anything =
      delLayers.length || delObjects.length || putLayers.length || putObjects.length || putCursors.length || delCursors.length || droppedOps.length;
    if (anything) {
      void gen.run(async (db) => {
        if (delLayers.length) {
          await db.objects.where('layerId').anyOf(delLayers).delete();
          await db.layers.bulkDelete(delLayers);
          await db.cursors.bulkDelete(delLayers);
          await db.prefs.bulkDelete(delLayers);
        }
        if (delObjects.length) await db.objects.bulkDelete(delObjects);
        if (putLayers.length) await db.layers.bulkPut(putLayers);
        if (putObjects.length) await db.objects.bulkPut(putObjects);
        if (delCursors.length) await db.cursors.bulkDelete(delCursors);
        if (putCursors.length) await db.cursors.bulkPut(putCursors);
        if (droppedOps.length) await db.deleteOps(droppedOps);
        await db.meta.put({ k: META.lastHlc, v: lastHlc });
      });
    }
    if (m.invalid) console.warn(`[annotations] skipped ${m.invalid} invalid annotation row(s)`);
    this.touch(layersChanged || activeGone);
    if (droppedOps.length) this.engine.emitState();
  }

  /** 403/404/410 for this sheet: it is no longer visible — forget other people's layers on it. */
  private onPullRejected(e: unknown): void {
    this.pullRejected = true;
    const status = e instanceof ApiFailure ? e.status : 0;
    if (status === 403 || status === 404 || status === 410) {
      const foreign = [...this.layers.values()].filter((l) => l.ownerId !== this.me.id).map((l) => l.id);
      if (foreign.length) {
        for (const id of foreign) {
          this.index.removeLayer(id);
          this.layers.delete(id);
          this.cursors.delete(id);
          this.resume.delete(id);
          this.prefs.delete(id);
          this.viewCache.delete(id);
        }
        void this.gen.run(async (db) => {
          await db.objects.where('layerId').anyOf(foreign).delete();
          await db.layers.bulkDelete(foreign);
          await db.cursors.bulkDelete(foreign);
          await db.prefs.bulkDelete(foreign);
        });
        this.index.invalidateAll();
      }
    }
    if (this.status === 'loading') this.status = this.localFailed ? 'error' : 'ready';
    this.touch(true);
  }

  /**
   * Repair after a failed local write (engine.repairLocal, inside its transaction): stores this
   * sheet's in-memory state again. Once the local load succeeded memory holds everything of the
   * sheet, so stored rows it no longer has (deletes whose write failed) are removed as well; after a
   * failed load only what memory has is written. Cursors memory dropped are dropped too (the next
   * pull then fetches those layers in full).
   */
  async persistAll(db: AnnotationDb): Promise<void> {
    if (this.dead || !this.loaded) return;
    const fileId = this.fileId;
    const layers = [...this.layers.values()];
    const layerIds = layers.map((l) => l.id);
    const live = layerIds.flatMap((id) => this.index.layerObjects(id));
    if (!this.localFailed) {
      const stored = (await db.layers.where('sheetFileId').equals(fileId).primaryKeys()) as string[];
      const gone = stored.filter((id) => !this.layers.has(id));
      if (gone.length) {
        await db.objects.where('layerId').anyOf(gone).delete();
        await db.layers.bulkDelete(gone);
        await db.cursors.bulkDelete(gone);
        await db.prefs.bulkDelete(gone);
      }
      if (layerIds.length) {
        const liveIds = new Set(live.map((o) => o.id));
        const storedObjects = (await db.objects.where('layerId').anyOf(layerIds).primaryKeys()) as string[];
        const dead = storedObjects.filter((id) => !liveIds.has(id));
        if (dead.length) await db.objects.bulkDelete(dead);
      }
    }
    await db.layers.bulkPut(layers);
    await db.objects.bulkPut(live);
    const cursors: CursorRow[] = [];
    for (const [layerId, seq] of this.cursors) if (this.layers.has(layerId)) cursors.push(cursorRow(layerId, seq));
    await db.cursors.bulkDelete(layerIds.filter((id) => !this.cursors.has(id)));
    await db.cursors.bulkPut(cursors);
    await db.prefs.bulkPut([...this.prefs].filter(([id]) => this.layers.has(id)).map(([layerId, visible]) => ({ layerId, visible })));
    if (this.activeLayerId) await db.meta.put({ k: META.active(fileId), v: this.activeLayerId });
    if (this.hideStale) await db.meta.put({ k: META.hideStale(fileId), v: { shown: [...this.staleShown] } });
    else if (!this.localFailed) await db.meta.delete(META.hideStale(fileId));
  }

  /** Drops everything of this sheet from memory (the engine clears Dexie and the outbox). */
  purgeLocal(): void {
    for (const id of this.layers.keys()) this.index.removeLayer(id);
    this.layers.clear();
    this.cursors.clear();
    this.resume.clear();
    this.prefs.clear();
    this.hideStale = false;
    this.staleShown.clear();
    this.viewCache.clear();
    this.history.clear();
    this.activeLayerId = null;
    this.index.invalidateAll();
    this.touch(true);
  }
}
